import { cn } from '@hbs-us/ui';

import { useAccess } from '../../auth/useAccess.js';

import { DispatcherApprovals } from './components/DispatcherApprovals.js';
import { DispatcherOnClock } from './components/DispatcherOnClock.js';
import { DispatcherQc } from './components/DispatcherQc.js';
import { DispatcherToday } from './components/DispatcherToday.js';
import { HomeGreeting } from './components/HomeGreeting.js';

/**
 * Home, for somebody running the day rather than reading the money.
 *
 * WHO GETS IT. Anybody who can read the dispatch board (`jobs.view`) and cannot read the overview
 * (`reports.view`). A default Manager holds both and gets the owner layout; this is the layout for
 * a Manager whose tenant took the money away, and it is the one the board belongs on.
 *
 * THREE REQUESTS, ALL ALREADY IN THE APP. `GET /api/v1/dispatch` feeds today's assignments,
 * `GET /api/v1/timesheets/week-grid` feeds BOTH the on-clock roster and the approval queue from one
 * response, and `GET /api/v1/qc/checklists?status=submitted` feeds the review queue. Nothing here
 * needed a new endpoint.
 *
 * ⚠ FLEET ALERTS ARE DELIBERATELY ABSENT. The expiry list a dispatcher would want is computed by
 * the server and returned on `/api/v1/overview`, which is exactly the endpoint this viewer cannot
 * read. `GET /api/v1/fleet` returns the raw vehicle rows instead, so putting the card here would
 * mean re-deriving the expiry windows in the browser — a second copy of a rule that already exists
 * on the server, free to drift from it. It waits for the server to expose the alerts on a route
 * this viewer holds.
 *
 * EVERY CARD IS GATED BY THE RULE ITS OWN ENDPOINT APPLIES. A tenant may rewrite the Manager preset,
 * so a card whose gate is assumed is a card that answers 403 for somebody.
 */
export function DispatcherHomeView() {
  const canAccess = useAccess();
  // `jobs.view` is what selected this layout, so today's board is always shown. The other three are
  // separate keys a tenant may hold back independently.
  const showOnClock = canAccess({ permission: 'time.view' });
  const showApprovals = canAccess({ permission: 'time.approve' });
  const showQc = canAccess({ permission: 'qc.view' });
  const secondRow = [showOnClock, showApprovals, showQc].filter(Boolean).length;

  return (
    <div className="min-h-full bg-background px-4 py-6 sm:px-6 lg:px-8">
      <HomeGreeting />

      <div className="grid gap-5">
        <DispatcherToday />

        {/*
          The column count follows how many cards a viewer actually holds, so a single card never
          sits in a third of the width with two empty thirds beside it. Every card in the row
          stretches to the tallest, so the headings and the links sit on the same lines across it.
        */}
        {secondRow > 0 ? (
          <div
            className={cn(
              'grid gap-5',
              secondRow === 2 && 'lg:grid-cols-2',
              secondRow === 3 && 'lg:grid-cols-3',
            )}
          >
            {showOnClock ? <DispatcherOnClock /> : null}
            {showApprovals ? <DispatcherApprovals /> : null}
            {showQc ? <DispatcherQc /> : null}
          </div>
        ) : null}

        {/*
          TODO(blocked on GET /api/v1/fleet/alerts): the fleet expiry card.

          See the ⚠ note in this file's header for why it is not built from `GET /api/v1/fleet`:
          the expiry windows are computed by the server on `/api/v1/overview`, which is the one
          endpoint this viewer cannot read, and re-deriving them here would be a second copy of a
          rule that is free to drift from the first. It waits for the route above.
        */}
      </div>
    </div>
  );
}
